import { capitalize, startCase, toLower } from 'lodash';
import moment from 'moment';
import { decode } from 'html-entities';

import { InputTypeEnum, PackageDetailsTempItemDto, TriggerTypeEnum } from '@pimm/services/lib/sms-formpack/types';
import { TemperatureUnitEnum } from '@pimm/services/lib/sms-tenants';
import { ItemStatusEnum, ViolationTypeEnum } from '@pimm/services/lib/store-inspections';
import { InputEntry } from './types';
import { IThemeColors, ThemeColors } from '@pimm/base';
import { useAppGlobalsGoals } from '../app';

export const formatName = (firstName?: string, lastName?: string): string => {
  return startCase(toLower([firstName, lastName].filter(Boolean).join(' ')));
};

export const getTempUnit = (): string => {
  const unitSymbol = useAppGlobalsGoals.getState()?.temperatureUnit === TemperatureUnitEnum.Celsius ? 'C' : 'F';
  return `${decode('&deg;')}${unitSymbol}`;
};

export const tempValue = (value?: number | string, includeUnit: boolean = true): string => {
  const temp: number = isNaN(Number(value)) ? 0 : Number(value);
  return `${temp.toFixed(1)}${includeUnit ? `${getTempUnit()}` : ''}`;
};

export const SkipDefaultValues: Partial<InputEntry> = {
  numericReading: -1,
  textReading: 'n/a',
} as const;

export const FormPallete: {
  [key in ItemStatusEnum | ViolationTypeEnum | 'Good']?: {
    dark?: string;
    main?: string;
    light?: string;
    contrastText?: string;
  };
} = {
  Completed: {
    dark: ThemeColors.blueLight[500],
    main: ThemeColors.blueLight[300],
    light: ThemeColors.blueLight[100],
    contrastText: 'black',
  },
  CompletedWithViolation: {
    dark: ThemeColors.error[500],
    main: ThemeColors.error[300],
    light: ThemeColors.error[100],
    contrastText: 'black',
  },
  FoodSafety: {
    dark: ThemeColors.error[500],
    main: ThemeColors.error[300],
    light: ThemeColors.error[100],
    contrastText: 'black',
  },
  QualityAssurance: {
    dark: ThemeColors.warning[400],
    main: ThemeColors.warning[300],
    light: ThemeColors.warning[100],
    contrastText: 'black',
  },
};

export const isTempProbeManual = (inputType?: InputTypeEnum) => {
  return [InputTypeEnum.TempAutoManual, InputTypeEnum.TempAutoProbeManual, InputTypeEnum.TempProbeManual].includes(
    inputType ?? InputTypeEnum.None,
  );
};

export const getInputDisplay = (
  _input: { textReading?: string; numericReading?: number },
  _inputType?: InputTypeEnum,
): string | undefined => {
  try {
    if (SkipDefaultValues.textReading === _input?.textReading) {
      return SkipDefaultValues.textReading?.toUpperCase(); // N/A
    }

    if (_input) {
      switch (_inputType) {
        case InputTypeEnum.YesNo:
        case InputTypeEnum.YesNoAndNa:
          return capitalize(_input.textReading);

        case InputTypeEnum.Decimal:
        case InputTypeEnum.Integer:
          return _input.numericReading?.toString();

        case InputTypeEnum.DateTime:
          const dateTime = moment(_input.textReading);
          return dateTime.isValid() ? dateTime.format('MM/DD/YYYY hh:mm A') : _input.textReading?.toUpperCase();

        case InputTypeEnum.Date:
        case InputTypeEnum.ExpirationDate:
          if (!_input?.textReading) return undefined;
          const date = moment(_input.textReading);
          return date.isValid() ? date.format('MMM DD, YYYY') : _input.textReading?.toUpperCase();

        case InputTypeEnum.TimeOnly:
        case InputTypeEnum.ExpirationTime:
          const time = moment(_input.textReading);
          return time.isValid() ? time.format('hh:mm A') : _input.textReading?.toUpperCase();

        case InputTypeEnum.EmployeesAll:
        case InputTypeEnum.EmployeesCrew:
        case InputTypeEnum.EmployeesMgmt:
          const [userId, firstName, lastName] = _input.textReading?.split('$') ?? [];
          return formatName(firstName, lastName);

        case InputTypeEnum.TempAutoManual:
        case InputTypeEnum.TempAutoProbeManual:
        case InputTypeEnum.TempProbeManual:
          return tempValue(_input.numericReading);

        case InputTypeEnum.Text:
          return _input?.textReading ?? '';

        default:
          return '';
      }
    }
    return '';
  } catch (error) {
    return '';
  }
};

export const violationPallete = (colors: IThemeColors, violation?: ViolationTypeEnum) => {
  const pallete = {
    [ViolationTypeEnum.None]: {
      dark: colors.blueLight[500],
      main: colors.blueLight[300],
      light: colors.blueLight[100],
      contrastText: 'black',
    },
    [ViolationTypeEnum.FoodSafety]: {
      dark: colors.error[500],
      main: colors.error[300],
      light: colors.error[100],
      contrastText: 'black',
    },
    [ViolationTypeEnum.QualityAssurance]: {
      dark: colors.warning[400],
      main: colors.warning[300],
      light: colors.warning[100],
      contrastText: 'black',
    },
    ['unknown']: {
      dark: colors.gray[600],
      main: colors.gray[300],
      light: colors.gray[100],
      contrastText: 'black',
    },
  };

  return pallete[violation ?? 'unknown'];
};

// Map of known base names (instance description) to translation keys
export const translationMap = {
  'Bacon Cooler': 'bacon_cooler',
  'Beverage Reach-in Cooler': 'beverage_reach_in_cooler',
  'Beverage Reach-in Cooler PUW': 'beverage_reach_in_cooler_puw',
  'Breading Station': 'breading_station',
  'Bun Freezer': 'bun_freezer',
  'Counter Reach-in Cooler': 'counter_reach_in_cooler',
  'Dining Room': 'dining_room',
  'Fry Station Freezer': 'fry_station_freezer',
  'Fry Station Spa Cooler': 'fry_station_spa_cooler',
  'Kitchen Temperature': 'kitchen_temperature',
  'Meat Well Cooler': 'meat_well_cooler',
  'Outdoor Temperature': 'outdoor_temperature',
  'Outside Bun Freezer Door Magnetic Field': 'outside_bun_freezer_door_magnetic_field',
  'PUW Station': 'puw_station',
  'Salad Reach-in Cooler PUW': 'salad_reach_in_cooler_puw',
  'SL-FR Reach-in Cooler': 'sl_fr_reach_in_cooler',
  'SL-PUW Reach-in Cooler': 'sl_puw_reach_in_cooler',
  'Upright Reach-in Cooler': 'upright_reach_in_cooler',
  'Upright Reach-in Freezer': 'upright_reach_in_freezer',
  'Upright Freezer': 'upright_freezer',
  'Walk-in Cooler': 'walk_in_cooler',
  'Walk-in Cooler Door Magnetic Field': 'walk_in_cooler_door_magnetic_field',
  'Walk-in Freezer': 'walk_in_freezer',
  'Walk-in Freezer Door Magnetic Field': 'walk_in_freezer_door_magnetic_field',
} as const;

// Mapping for specific descriptions to new values
export const descriptionMapping = {
  'Beverage Reach-in Cooler PUW': 'PUW Station',
  'Dining Room Temperature': 'Dining Room',
  'Upright Reach-in Freezer II': 'Upright Freezer',
  'Upright Reach-in Freezer III': 'Upright Freezer 2',
  'Walk-in Cooler Door Magnetic Field': 'Walk-in Cooler Door',
  'Walk-in Freezer Door Magnetic Field': 'Walk-in Freezer Door',
} as const;
